/**
 * Photo-to-Design frontend controller
 * Works with /ai-backend/routes/redesign.js
 */
(() => {
  // Auto-detect backend host: localhost during dev, subdomain in production.
  const API_BASE = (() => {
    const h = window.location.hostname;
    if (h === "localhost" || h === "127.0.0.1" || h.endsWith(".local")) {
      return "http://localhost:3007";
    }
    return "https://ai.sanizone.nl";
  })();

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];

  const fileInput = $('#redesign-file');
  const dropZone = $('#redesign-dropzone');
  const preview = $('#redesign-preview');
  const previewImg = $('#redesign-preview-img');
  const previewClear = $('#redesign-preview-clear');
  const stylePicker = $('#redesign-style-picker');
  const generateBtn = $('#redesign-generate');
  const resultsMount = $('#redesign-results');
  const progressFill = $$('#redesign-progress span');
  const progressLabel = $('#redesign-progress-label');

  if (!fileInput || !dropZone) return;

  let selectedFile = null;
  let selectedStyle = "japandi";

  // Pre-select style from URL param (e.g. /redesign/?style=japandi from scan tool)
  const _urlStyle = new URLSearchParams(location.search).get("style");
  if (_urlStyle) selectedStyle = _urlStyle;

  // Bundle pre-load: ?bundle=japandi-set-mat-zwart links from product pages
  const _urlBundle = new URLSearchParams(location.search).get("bundle");
  if (_urlBundle && /^[a-z0-9-]+$/.test(_urlBundle)) {
    if (_urlBundle.startsWith("japandi-set-")) selectedStyle = "japandi";
    const niceName = _urlBundle
      .replace(/-/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
    const banner = document.createElement("div");
    banner.className = "max-w-3xl mx-auto mt-6 mb-2 mx-6 md:mx-auto px-5 py-4 rounded-2xl bg-[color:var(--moss)]/10 border border-[color:var(--moss)]/30 flex items-start gap-3";
    banner.innerHTML = `
      <svg class="w-5 h-5 mt-0.5 text-[color:var(--moss)] shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
      <div class="flex-1 text-[14px] leading-[1.5] text-[color:var(--ink-soft)]">
        <p class="font-semibold text-ink mb-0.5">Bundel voorgeladen — ${niceName}</p>
        <p>Upload je badkamer-foto en de AI gebruikt deze set als inspiratie voor de render. Stijl: <span class="text-[color:var(--moss)] font-semibold">Japandi</span>.</p>
      </div>
    `;
    if (dropZone && dropZone.parentNode) {
      dropZone.parentNode.insertBefore(banner, dropZone);
    }
  }

  function setProgressStep(n, label) {
    progressFill.forEach((el, i) => {
      el.className = i < n ? "flex-1 h-1 bg-ink rounded-full" : "flex-1 h-1 bg-[color:var(--line)] rounded-full";
    });
    if (progressLabel) progressLabel.textContent = label;
  }

  function onFileSelected(file) {
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      alert("Kies een afbeelding (JPG, PNG of HEIC).");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert("Foto is te groot. Max 10 MB.");
      return;
    }
    selectedFile = file;
    const reader = new FileReader();
    reader.onload = (e) => {
      previewImg.src = e.target.result;
      preview.classList.remove("hidden");
      dropZone.classList.add("hidden");
      if (stylePicker) stylePicker.classList.remove("hidden");
      if (generateBtn) generateBtn.removeAttribute("disabled");
      setProgressStep(2, "Stap 2/3 — AI analyseert je foto…");
      // Auto-start generation after brief delay so preview animation completes
      setTimeout(() => generate(), 900);
    };
    reader.readAsDataURL(file);
  }

  fileInput.addEventListener("change", (e) => onFileSelected(e.target.files?.[0]));

  dropZone.addEventListener("dragover", (e) => {
    e.preventDefault();
    dropZone.classList.add("ring-2", "ring-[color:var(--moss)]");
  });
  dropZone.addEventListener("dragleave", () => dropZone.classList.remove("ring-2", "ring-[color:var(--moss)]"));
  dropZone.addEventListener("drop", (e) => {
    e.preventDefault();
    dropZone.classList.remove("ring-2", "ring-[color:var(--moss)]");
    onFileSelected(e.dataTransfer.files?.[0]);
  });

  if (previewClear) {
    previewClear.addEventListener("click", () => {
      selectedFile = null;
      fileInput.value = "";
      preview.classList.add("hidden");
      dropZone.classList.remove("hidden");
      if (stylePicker) stylePicker.classList.add("hidden");
      if (generateBtn) generateBtn.setAttribute("disabled", "true");
      if (resultsMount) resultsMount.innerHTML = "";
      setProgressStep(1, "Stap 1/3 — je foto blijft privé");
    });
  }

  function activateStyleBtn(style) {
    if (!stylePicker) return;
    $$("[data-style]", stylePicker).forEach((b) => {
      const active = b.dataset.style === style;
      b.classList.toggle("bg-ink", active);
      b.classList.toggle("text-bone", active);
      b.classList.toggle("bg-bone", !active);
      b.classList.toggle("text-ink", !active);
    });
  }

  if (stylePicker) {
    stylePicker.addEventListener("click", (e) => {
      const btn = e.target.closest("[data-style]");
      if (!btn) return;
      selectedStyle = btn.dataset.style;
      activateStyleBtn(selectedStyle);
    });
    // Pre-select from URL param
    if (_urlStyle) activateStyleBtn(_urlStyle);
  }

  async function generate() {
    if (!selectedFile) return;
    if (!resultsMount) return;
    generateBtn.setAttribute("disabled", "true");
    generateBtn.textContent = "Bezig…";
    setProgressStep(3, "Stap 3/3 — AI werkt (±25 seconden)");

    resultsMount.innerHTML = skeletonMarkup();
    resultsMount.scrollIntoView({ behavior: "smooth", block: "start" });

    // Simulated progress driver — advances through phases until real response
    const progress = startProgressDriver();

    const form = new FormData();
    form.append("photo", selectedFile);
    form.append("style", selectedStyle);
    if (_urlBundle) form.append("bundle", _urlBundle);

    try {
      const res = await fetch(`${API_BASE}/api/redesign`, {
        method: "POST",
        body: form,
      });
      const data = await res.json();
      progress.finish();
      if (!res.ok) {
        const hints = {
          no_bathroom: "Probeer een foto waarop de hele badkamer zichtbaar is, inclusief wanden en vloer.",
          ai_unavailable: "Onze AI is even overbelast. Wacht een minuut en probeer het opnieuw.",
          rate_limit: "Je kunt maximaal 3 renders per uur aanvragen. Kom straks terug.",
          daily_limit: "Het dagelijkse maximum is bereikt. Morgen kun je opnieuw renders aanvragen.",
          invalid_file_type: "Kies een foto in JPG, PNG of HEIC formaat.",
        };
        const hint = hints[data.error] || "Soms helpt het om een foto te kiezen waarop de hele ruimte goed zichtbaar is.";
        resultsMount.innerHTML = errorMarkup(data.message || "Er ging iets mis.", hint);
        generateBtn.removeAttribute("disabled");
        generateBtn.textContent = "Genereer mijn badkamer";
        return;
      }
      // Highlight detected style from AI analysis
      if (data.analysis?.detected_style && stylePicker) {
        const detected = data.analysis.detected_style;
        selectedStyle = detected;
        $$("[data-style]", stylePicker).forEach((b) => {
          const active = b.dataset.style === detected;
          b.classList.toggle("bg-ink", active);
          b.classList.toggle("text-bone", active);
          b.classList.toggle("bg-bone", !active);
          b.classList.toggle("text-ink", !active);
        });
      }
      resultsMount.innerHTML = resultsMarkup(data, API_BASE);
      resultsMount.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (err) {
      progress.finish();
      console.error(err);
      resultsMount.innerHTML = errorMarkup(
        "Kon geen verbinding maken met de AI-server.",
        "Controleer je internetverbinding en probeer het opnieuw."
      );
    } finally {
      generateBtn.removeAttribute("disabled");
      generateBtn.textContent = "Genereer opnieuw";
    }
  }

  function startProgressDriver() {
    const phases = [
      { at: 0, pct: 5, label: "Foto ontvangen — ruimte wordt geanalyseerd…" },
      { at: 3000, pct: 22, label: "Stijl en categorieën herkennen…" },
      { at: 7000, pct: 38, label: "Producten uit collectie matchen…" },
      { at: 10000, pct: 55, label: "Render 1 van 3 wordt geschilderd…" },
      { at: 17000, pct: 75, label: "Render 2 van 3 wordt geschilderd…" },
      { at: 23000, pct: 90, label: "Laatste render — details en licht…" },
      { at: 32000, pct: 96, label: "Bijna klaar…" },
    ];
    const start = Date.now();
    let done = false;
    const interval = setInterval(() => {
      if (done) return;
      const t = Date.now() - start;
      let current = phases[0];
      for (const p of phases) if (t >= p.at) current = p;
      setProgress(current.pct, current.label);
    }, 250);
    return {
      finish() {
        done = true;
        clearInterval(interval);
        setProgress(100, "Klaar — je badkamer staat hieronder.");
      },
    };
  }

  function setProgress(pct, label) {
    const bar = document.querySelector("#redesign-progress-bar-fill");
    const lbl = document.querySelector("#redesign-progress-bar-label");
    const pctEl = document.querySelector("#redesign-progress-bar-pct");
    if (bar) bar.style.width = Math.min(100, Math.max(0, pct)) + "%";
    if (lbl) lbl.textContent = label;
    if (pctEl) pctEl.textContent = Math.round(pct) + "%";
  }

  if (generateBtn) generateBtn.addEventListener("click", generate);

  function skeletonMarkup() {
    // Inline style via CSS-variabelen zodat Tailwind JIT niet in de weg zit
    // (classes gegenereerd na pageload worden niet meer opgepikt door de CDN build)
    const sectionStyle = "background: var(--cream); color: var(--ink); padding: 80px 0 96px;";
    const inner = "max-width: 1280px; margin: 0 auto; padding: 0 24px;";
    const label = "font-size: 11px; letter-spacing: 0.2em; text-transform: uppercase; color: var(--muted); margin-bottom: 14px;";
    const bar = "height: 6px; background: rgba(26,26,26,0.08); border-radius: 999px; overflow: hidden; margin-top: 24px;";
    const fill = "height: 100%; background: var(--moss); width: 5%; transition: width 0.6s cubic-bezier(.22,.61,.36,1);";
    const card = `
      <div style="position: relative; border-radius: 20px; overflow: hidden; background: var(--paper); aspect-ratio: 4/5;">
        <div style="position: absolute; inset: 0; background: linear-gradient(90deg, rgba(201,168,123,0) 0%, rgba(201,168,123,0.18) 50%, rgba(201,168,123,0) 100%); background-size: 200% 100%; animation: sh-skel 1.8s ease-in-out infinite;"></div>
      </div>`;
    return `
      <section style="${sectionStyle}">
        <div style="${inner}">
          <div style="display: flex; align-items: baseline; justify-content: space-between; gap: 20px; flex-wrap: wrap;">
            <p style="${label}">AI aan het werk</p>
            <p id="redesign-progress-bar-pct" style="font-family: 'Inter', ui-sans-serif, system-ui; font-weight: 600; font-size: 28px; letter-spacing: -0.02em; color: var(--ink); margin: 0;">5%</p>
          </div>
          <p id="redesign-progress-bar-label" style="font-family: 'Inter', ui-sans-serif, system-ui; font-weight: 600; font-size: 26px; line-height: 1.2; letter-spacing: -0.02em; color: var(--ink); margin: 8px 0 0; max-width: 720px;">Foto ontvangen — ruimte wordt geanalyseerd…</p>
          <div style="${bar}">
            <div id="redesign-progress-bar-fill" style="${fill}"></div>
          </div>
          <p style="font-size: 13px; color: var(--muted); margin-top: 14px;">Dit duurt meestal 25 tot 35 seconden. Laat de pagina open staan.</p>
          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 24px; margin-top: 48px;">${card}${card}${card}</div>
        </div>
      </section>
      <style>
        @keyframes sh-skel { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
      </style>
    `;
  }

  function errorMarkup(msg, hint) {
    const hintText = hint || "Soms helpt het om een foto te kiezen waarop de hele ruimte goed zichtbaar is.";
    return `
      <section style="background: var(--cream); color: var(--ink); padding: 96px 0;">
        <div style="max-width: 720px; margin: 0 auto; padding: 0 24px; text-align: center;">
          <p style="font-size: 11px; letter-spacing: 0.2em; text-transform: uppercase; color: var(--muted); margin-bottom: 12px;">Helaas</p>
          <h3 style="font-family: 'Inter', ui-sans-serif, system-ui; font-weight: 600; font-size: 24px; letter-spacing: -0.02em; color: var(--ink); margin: 0 0 12px;">${escapeHtml(msg)}</h3>
          <p style="font-size: 15px; color: var(--muted); margin: 0;">${escapeHtml(hintText)}</p>
        </div>
      </section>`;
  }

  function resultsMarkup(data, base) {
    const { renders, bundle, style } = data;
    const skus = renders[0]?.skus || [];
    const ctaStyle = "display: inline-flex; align-items: center; gap: 10px; background: var(--ink); color: var(--bone); padding: 16px 28px; border-radius: 999px; font-size: 15px; text-decoration: none; transition: background 0.3s ease, transform 0.2s ease;";
    const bundleHref = bundle?.resolved ? bundle.cartUrl : "#redesign-products";
    const bundleLabel = bundle?.resolved
      ? `Voeg de hele set toe aan winkelwagen · €${fmt(bundle.total)}`
      : `Bekijk de producten · €${fmt(bundle.total)}`;

    const renderCards = renders.map((r, i) => `
      <figure style="margin: 0;">
        <div style="position: relative; border-radius: 20px; overflow: hidden; background: var(--paper); aspect-ratio: 4/5;">
          <img src="${base}${r.url}" style="width: 100%; height: 100%; object-fit: cover; display: block;" alt="Render ${i + 1}" loading="lazy" />
          <div style="position: absolute; top: 16px; left: 16px; padding: 6px 12px; border-radius: 999px; background: rgba(250,250,247,0.85); backdrop-filter: blur(8px); color: var(--ink); font-size: 11px; letter-spacing: 0.2em; text-transform: uppercase;">Variant ${i + 1}</div>
        </div>
      </figure>
    `).join("");

    const productCards = skus.map((s) => `
      <a href="${s.url}" style="display: block; text-decoration: none; color: inherit;">
        <div style="position: relative; border-radius: 16px; overflow: hidden; background: var(--paper); aspect-ratio: 1/1;">
          <img src="${s.image}" style="width: 100%; height: 100%; object-fit: cover; display: block;" alt="${escapeHtml(s.title)}" loading="lazy" />
        </div>
        <p style="font-size: 11px; letter-spacing: 0.2em; text-transform: uppercase; color: var(--muted); margin: 12px 0 4px;">${escapeHtml(s.category)}</p>
        <p style="font-size: 14px; line-height: 1.4; color: var(--ink); margin: 0 0 4px; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;">${escapeHtml(s.title)}</p>
        <p style="font-size: 14px; font-weight: 500; color: var(--ink); margin: 0;">€${fmt(s.price)}</p>
      </a>
    `).join("");

    return `
      <section style="background: var(--bone); color: var(--ink); padding: 96px 0 112px;">
        <div style="max-width: 1280px; margin: 0 auto; padding: 0 24px;">
          <p style="font-size: 11px; letter-spacing: 0.2em; text-transform: uppercase; color: var(--muted); margin: 0 0 14px;">Stijl · ${escapeHtml(style)}</p>
          <h2 style="font-family: 'Inter', ui-sans-serif, system-ui; font-weight: 600; font-size: clamp(40px, 6vw, 64px); line-height: 1.05; letter-spacing: -0.03em; color: var(--ink); margin: 0;">Je badkamer, <em style="font-style: italic; color: var(--oak);">opnieuw bekeken</em>.</h2>
          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 20px; margin-top: 44px;">${renderCards}</div>
        </div>
      </section>
      <section id="redesign-products" style="background: var(--cream); color: var(--ink); padding: 96px 0;">
        <div style="max-width: 1280px; margin: 0 auto; padding: 0 24px;">
          <div style="display: flex; flex-direction: column; gap: 24px; margin-bottom: 44px;">
            <div style="display: flex; flex-wrap: wrap; align-items: flex-end; justify-content: space-between; gap: 24px;">
              <div>
                <p style="font-size: 11px; letter-spacing: 0.2em; text-transform: uppercase; color: var(--muted); margin: 0 0 10px;">De set</p>
                <h3 style="font-family: 'Inter', ui-sans-serif, system-ui; font-weight: 600; font-size: clamp(28px, 4vw, 44px); line-height: 1.1; letter-spacing: -0.02em; color: var(--ink); margin: 0;">${skus.length} producten uit onze collectie.</h3>
              </div>
              <a href="${bundleHref}" style="${ctaStyle}" onmouseover="this.style.background='var(--moss)'" onmouseout="this.style.background='var(--ink)'">${bundleLabel}</a>
            </div>
          </div>
          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 24px;">${productCards}</div>
        </div>
      </section>
    `;
  }

  function fmt(n) {
    return Number(n || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  function escapeHtml(s) {
    return String(s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
  }
})();
